// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import { Icon, IconName, Text } from '@usealloy/component-library';
import find from 'lodash/find';
import moment from 'moment';

import type { AdminCustomerAccess } from '~/dashboard/context';
import { UserContext } from '~/dashboard/context';
import { kebabToCaps } from '~/helpers/formatting';
import { MessageWrapper } from './components/MessageWrapper';

const CustomerAccessBanner = () => {
  const {
    adminCustomerAccess,
    adminPermissions,
    agent: { isEmployee, isAfefParentAgent: agentIsParent },
    customer: {
      cidDisclaimerMessage,
      organizationName,
      organizationNameSlug,
      isAfefChildOrganization: customerIsChild,
    },
    alloyEnvironment,
    featureFlags,
    hasBetaRoutesCookie,
  } = useContext(UserContext);
  const isBetaRoutesEnabled = Boolean(featureFlags?.betaRoutesEnabled);

  const inAdminDashboard = window.location.pathname.startsWith(
    '/v3/dashboard/admin',
  );

  const customerAccessRecord = useMemo(
    (): AdminCustomerAccess | null => find(adminCustomerAccess, { organizationNameSlug }),
    [organizationNameSlug, adminCustomerAccess],
  );

  const displayExpirationTimer = useMemo((): String => {
    if (!customerAccessRecord || !adminPermissions) return '';
    const shouldDisplayTimer = !adminPermissions['READ:Customers:null']
      && !customerAccessRecord?.isPermanent
      && customerAccessRecord?.expiredAt;
    if (!shouldDisplayTimer) return '';
    return moment(customerAccessRecord?.expiredAt).format('M/D/YY h:mmA');
  }, [adminPermissions, customerAccessRecord]);

  const isParentAccessingChild = agentIsParent && customerIsChild;

  return useMemo((): JSX.Element => {
    if (
      featureFlags?.settingsShowCidConfigurationField
      && cidDisclaimerMessage
    ) {
      return (
        <MessageWrapper>
          <Text weight={500} data-testid="customer-access-cid-disclaimer">
            {cidDisclaimerMessage}
          </Text>
        </MessageWrapper>
      );
    }

    if ((!isEmployee && !isParentAccessingChild) || inAdminDashboard) {
      return null;
    }
    const status = isEmployee ? 'danger' : 'info';

    const formattedAlloyEnvironment = kebabToCaps(alloyEnvironment);

    return (
      <>
        {/* Show the banner only in the legacy (non-beta) dashboard; beta routes render this data in the new nav and don't need this banner */}
        {isBetaRoutesEnabled && hasBetaRoutesCookie ? null : (
          <MessageWrapper status={status}>
            <>
              <Text weight={500} data-testid="customer-access-banner-text">
                {`${formattedAlloyEnvironment} - ${organizationName}`}
              </Text>
              {Boolean(displayExpirationTimer.length) && (
                <>
                  <Icon icon={IconName.Timer} size="small" />
                  <Text
                    weight={500}
                    data-testid="customer-access-banner-expiration"
                  >
                    {displayExpirationTimer}
                  </Text>
                </>
              )}
            </>
          </MessageWrapper>
        )}
      </>
    );
  }, [
    featureFlags,
    cidDisclaimerMessage,
    isEmployee,
    isParentAccessingChild,
    inAdminDashboard,
    alloyEnvironment,
    organizationName,
    displayExpirationTimer,
    isBetaRoutesEnabled,
    hasBetaRoutesCookie,
  ]);
};

export default CustomerAccessBanner;
